import { useState } from 'react';
import { ArrowRightIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { createEarlyAdopterHandler } from '@/utils/earlyAdopterInterceptor';
import { usePricingData } from '@/hooks/usePricingData';

interface OutcomeFeature {
  title: string;
  description: string;
}

const thriveMaxFeatures: OutcomeFeature[] = [
  {
    title: "Identify Hazards Early",
    description: "Unlimited ThriveVision room scans to spot risks before they become emergencies."
  },
  {
    title: "Quantify Your Wellness",
    description: "ThriveScore transforms daily activity into a clear measure of safety and independence."
  },
  {
    title: "Master Your Routine",
    description: "MyDailyCheckIn and smart reminders build the habits that keep you steady."
  },
  {
    title: "Ensure Medication Accuracy",
    description: "A shared medication log that provides certainty for you and peace of mind for your family."
  },
  {
    title: "Invite One Family Member",
    description: "Shared visibility for your primary point of contact."
  }
];

const thriveFamilyFeatures: OutcomeFeature[] = [
  {
    title: "Eliminate Sibling Guesswork",
    description: "Up to 5 family members connected to one shared dashboard."
  },
  {
    title: "Coordinate Without Chaos",
    description: "Messaging and task collaboration to end \"who's doing what?\" confusion."
  },
  {
    title: "Proactive Care Alignment",
    description: "Shared tools that support independence without micromanaging."
  },
  {
    title: "Clarity at a Glance",
    description: "Real-time visibility into safety updates and wellness trends."
  }
];

type BillingCycle = 'monthly' | 'yearly';

const DualPathPlans = () => {
  const { openModal } = useEarlyAdopter();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const { getPlan } = usePricingData();

  const max = getPlan('thrivemax');
  const family = getPlan('thrivefamily');

  const maxMonthly = max?.monthlyPrice ?? 19;
  const maxAnnual = max?.annualPrice ?? 205;
  const maxYearlyEquiv = max?.monthlyEquivalent ?? Math.round(maxAnnual / 12);

  const famMonthly = family?.monthlyPrice ?? 49;
  const famAnnual = family?.annualPrice ?? 529;
  const famYearlyEquiv = family?.monthlyEquivalent ?? Math.round(famAnnual / 12);

  const isYearly = billingCycle === 'yearly';

  return (
    <div className="mb-16">
      <div className="text-center mb-10">
        <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-3">
          Select Your Starting Point
        </h2>
        <p className="text-muted-foreground text-lg max-w-xl mx-auto">
          Two ways to activate your circle of support. Choose the foundation that fits your household today.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-8">
        <div
          className="inline-flex rounded-lg border-2 border-border bg-background p-1"
          role="radiogroup"
          aria-label="Billing cycle"
        >
          <button
            role="radio"
            aria-checked={!isYearly}
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-3 rounded-md text-base font-semibold transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              !isYearly
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-muted'
            }`}
          >
            Pay Monthly
          </button>
          <button
            role="radio"
            aria-checked={isYearly}
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-3 rounded-md text-base font-semibold transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              isYearly
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-muted'
            }`}
          >
            Pay Yearly (2 Months Free)
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
        {/* Path A: ThriveMax */}
        <Card className="relative border-2 border-primary/30 bg-gradient-to-b from-primary/5 to-transparent overflow-hidden flex flex-col">
          <CardContent className="pt-8 pb-8 flex flex-col flex-1">
            <Badge variant="outline" className="mb-4 text-xs border-primary/30 text-primary">
              For Independent Living
            </Badge>
            <h3 className="text-2xl font-bold text-foreground mb-1">ThriveMax</h3>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-bold text-foreground">${isYearly ? maxYearlyEquiv : maxMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4 min-h-[20px]">
              {isYearly ? `Billed annually at $${maxAnnual}` : '\u00A0'}
            </p>
            <p className="text-base font-medium text-foreground mb-2">
              Your daily structure for living safely at home.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              ThriveMax gives you visibility, accountability, and momentum — all in one place.
            </p>

            <div className="space-y-4 mb-6">
              {thriveMaxFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <ShieldCheckIcon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{feature.title}</p>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-1" />
            <p className="text-xs text-muted-foreground italic mb-4">
              Includes a 7-day trial. Continue only if it feels valuable.
            </p>

            <Button 
              size="lg" 
              className="w-full bg-primary hover:bg-primary/90"
              onClick={createEarlyAdopterHandler(openModal, 'Pricing - Request ThriveMax Access')}
            >
              Request ThriveMax Access
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Path B: ThriveFamily */}
        <Card className="relative border-2 border-circle-secondary/30 bg-gradient-to-b from-circle-secondary/5 to-transparent overflow-hidden flex flex-col">
          <div className="absolute top-0 right-0">
            <Badge className="rounded-none rounded-bl-lg bg-circle-secondary text-white border-0 px-3 py-1 text-xs">
              Most Popular
            </Badge>
          </div>
          <CardContent className="pt-8 pb-8 flex flex-col flex-1">
            <Badge variant="outline" className="mb-4 text-xs border-circle-secondary/30 text-circle-secondary">
              For Family Coordination
            </Badge>
            <h3 className="text-2xl font-bold text-foreground mb-1">ThriveFamily</h3>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-bold text-foreground">${isYearly ? famYearlyEquiv : famMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4 min-h-[20px]">
              {isYearly ? `Billed annually at $${famAnnual}` : '\u00A0'}
            </p>
            <p className="text-base font-medium text-foreground mb-2">
              Independence at home. Strength together.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              ThriveFamily brings everyone into one aligned, shared view of safety and care.
            </p>

            <p className="text-xs font-semibold text-foreground mb-3 uppercase tracking-wide">
              Everything in ThriveMax, plus:
            </p>

            <div className="space-y-4 mb-6">
              {thriveFamilyFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <ShieldCheckIcon className="w-5 h-5 text-circle-secondary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{feature.title}</p>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-1" />
            <div className="p-3 bg-circle-secondary/10 rounded-lg mb-4">
              <p className="text-xs text-foreground/80">
                <span className="font-semibold">Family-first activation:</span> Explore ThriveFamily free. Billing begins only after the older adult joins and activates the household.
              </p>
            </div>

            <Button 
              size="lg" 
              className="w-full bg-circle-secondary hover:bg-circle-secondary/90 text-white"
              onClick={createEarlyAdopterHandler(openModal, 'Pricing - Request ThriveFamily Access')}
            >
              Request ThriveFamily Access
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DualPathPlans;
